import React from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import Spinner from 'common/components/spinner';
import StoryboardReportRenderer from 'storyboard/components/storyboard-report-renderer';
import StoryboardScrollHint from 'storyboard/components/storyboard-scroll-hint';
import StoryboardError from 'storyboard/components/storyboard-error';
import {BOTTOM, TOP} from 'common/constants';
import {limitToRange} from 'common/utils/number-util';
import {getLinearGradient} from 'common/utils/style-util';
import './index.scss';

export default class StoryboardChapter extends React.Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    enums: PropTypes.object.isRequired,
    isAutoplaying: PropTypes.bool,
    location: PropTypes.object.isRequired,
    nextChapter: PropTypes.func,
    report: PropTypes.object.isRequired,
    scrollHintHeight: PropTypes.number,
  };

  static defaultProps = {
    isAutoplaying: false,
    nextChapter: () => {},
    report: null,
    scrollHintHeight: 50,
  };

  constructor() {
    super();
    this.state = {
      bottomScrollHintOpacity: 0,
      topScrollHintOpacity: 0,
    };
    this.handleScroll = this.handleScroll.bind(this);
    this.stopAutoplay = this.stopAutoplay.bind(this);
  }

  componentDidMount() {
    // Fake initial scroll event to set up the scroll hints:
    this.handleScroll({
      currentTarget: {
        clientHeight: 10,
        scrollHeight: Number.MAX_VALUE,
        scrollTop: 0,
      },
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Autoplay starts now:
    if (nextProps.isAutoplaying && !this.props.isAutoplaying) {
      this.startAutoplay(nextProps);
    }

    // Autoplay was active on the previous
    // Chapter and should continue:
    if (nextProps.isAutoplaying && !this.autoplay) {
      this.startAutoplay(nextProps);
    }

    if (!nextProps.isAutoplaying) {
      this.stopAutoplay();
    }
  }

  hasData(error, report, chapters) {
    return Boolean(!error && report?.data && chapters?.length);
  }

  startAutoplay(props) {
    console.log('Storyboard: start autoplay', props);
    const pause = () => {
      this.timer = setInterval(() => {
        clearInterval(this.timer);
        if (this.autoplay) {
          this.autoplay.next();
        }
      }, 10000);
    };

    const scroll = () => {
      const node = this._reportRenderer;
      if (node) {
        const incrementScroll = () => {
          node.scrollTop++;
          if (this.autoplay) {
            if (node.scrollTop + 1 >= node.scrollHeight - node.clientHeight) {
              this.autoplay.next();
            } else {
              window.requestAnimationFrame(incrementScroll);
            }
          }
        };
        window.requestAnimationFrame(incrementScroll);
      }
    };

    const nextChapter = () => {
      console.log('Storyboard: next chapter');
      this.stopAutoplay();
      props.nextChapter(props.location, props.data.chapters);
    };

    function* autoplayGenerator() {
      yield pause();
      yield scroll();
      yield pause();
      yield nextChapter();
    }
    this.autoplay = autoplayGenerator();
    this.autoplay.next();
  }

  stopAutoplay() {
    clearInterval(this.timer);
    this.autoplay = null;
  }

  render() {
    const {error} = this.props.report;
    const hasData = this.hasData(
      this.props.report.error,
      this.props.report,
      this.props.data.chapters,
    );
    return (
      <div
        className={'StoryboardChapter'}
        style={{
          background: getLinearGradient(
            this.props.data.configuration.theme.backgroundColors,
          ),
        }}>
        {/* Following wrapper workarounds Safari bug (scroll hint positioning) */}
        <div
          style={{
            bottom: 0,
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0,
          }}>
          {hasData && !this.props.report.loading ? (
            <StoryboardReportRenderer
              data={this.props.data}
              enums={this.props.enums}
              handleScroll={this.handleScroll}
              location={this.props.location}
              ref={(ref) => (this._reportRenderer = findDOMNode(ref))}
              report={this.props.report}
            />
          ) : (
            <StoryboardError error={error} />
          )}

          <StoryboardScrollHint
            height={this.props.scrollHintHeight}
            opacity={this.state.topScrollHintOpacity}
            position={TOP}
          />
          <StoryboardScrollHint
            height={this.props.scrollHintHeight}
            opacity={this.state.bottomScrollHintOpacity}
            position={BOTTOM}
          />

          {this.props.report.loading ? <Spinner /> : null}
        </div>
      </div>
    );
  }

  handleScroll(event) {
    const {clientHeight, scrollHeight, scrollTop} = event.currentTarget;
    const {scrollHintHeight} = this.props;
    this.setState({
      bottomScrollHintOpacity: limitToRange(
        (scrollHeight - clientHeight - scrollTop) / scrollHintHeight,
        [0, 1],
      ),
      topScrollHintOpacity: limitToRange(scrollTop / scrollHintHeight, [0, 1]),
    });
  }
}
